import { Select, type SelectProps } from 'antd';
import { useMemo, type FC } from 'react';
import {
  CompressedAirRegister,
  ElectricityRegister,
  GasRegister,
  type MeterRegister,
  OilRegister,
  CustomRegister,
  OtherRegister,
  RegisterMedium,
  WaterRegister,
  type StrictOmit,
  ThermalRegister,
} from '@enmon/commons';
import type { DefaultOptionType } from 'antd/lib/select';
import { isNotNil } from 'ramda';
import { filterOption, filterSort } from '../../utils';

type ObisCodeMap = Readonly<Record<string, MeterRegister['obis']>>;

const OBIS_PER_MEDIUM_MAP: Readonly<Record<RegisterMedium, ObisCodeMap>> = {
  [RegisterMedium.Cold]: ThermalRegister,
  [RegisterMedium.Heating]: ThermalRegister,
  [RegisterMedium.ColdWater]: WaterRegister,
  [RegisterMedium.WarmWater]: WaterRegister,
  [RegisterMedium.Custom]: CustomRegister,
  [RegisterMedium.Electricity]: ElectricityRegister,
  [RegisterMedium.Gas]: GasRegister,
  [RegisterMedium.Nitrogen]: GasRegister,
  [RegisterMedium.Oil]: OilRegister,
  [RegisterMedium.Other]: OtherRegister,
  [RegisterMedium.PressedAir]: CompressedAirRegister,
} as const;

export interface RegisterOBISSelectOption extends DefaultOptionType {
  value: MeterRegister['obis'];
}

export interface RegisterOBISSelectProps
  extends StrictOmit<SelectProps<MeterRegister['obis']>, 'options' | 'optionLabelProp'> {
  medium?: RegisterMedium;
}

const formatObisDescription = (description: string): string =>
  description
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim();

const getObisDescription = (obisMap: ObisCodeMap, obis: MeterRegister['obis']): string | undefined => {
  const entry = Object.entries(obisMap).find(([, value]) => value === obis);
  return entry ? formatObisDescription(entry[0]) : undefined;
};

export const getRegisterObisDescription = ({
  medium,
  obis,
}: Pick<MeterRegister, 'medium' | 'obis'>): string | undefined => {
  const obisMap = OBIS_PER_MEDIUM_MAP[medium];
  return getObisDescription(obisMap, obis);
};

export const RegisterOBISSelect: FC<RegisterOBISSelectProps> = ({
  allowClear = true,
  showSearch = true,
  medium,
  ...propsToPass
}) => {
  const options = useMemo(() => {
    if (isNotNil(medium)) {
      const obisMap = OBIS_PER_MEDIUM_MAP[medium];

      return Object.values(obisMap).map<RegisterOBISSelectOption>(obis => {
        const description = getObisDescription(obisMap, obis);

        return {
          value: obis,
          label: description ? `${obis} - ${description}` : obis,
        };
      });
    }

    return [];
  }, [medium]);

  return (
    <Select
      allowClear={allowClear}
      filterOption={filterOption}
      filterSort={filterSort}
      showSearch={showSearch}
      {...propsToPass}
      options={options}
    />
  );
};
